import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldWarning, MagnifyingGlass, ArrowsClockwise, Wallet, Crown, Warning,
  CheckCircle, XCircle, ClockClockwise, UserCircle, Copy, Question, Plus, Minus, X,
} from "@phosphor-icons/react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtAge(min) {
  if (min == null) return "—";
  if (min < 60) return `${Math.round(min)} min ago`;
  if (min < 24 * 60) return `${(min / 60).toFixed(1)} hr ago`;
  return `${(min / (60 * 24)).toFixed(1)} d ago`;
}

// Currency-aware formatter for the lifetime-paid chip on the customer
// card. Uses Intl.NumberFormat so ₹1,20,000 and $1,200.00 both render
// with the correct grouping and symbol. Falls back to a `{cur} {amount}`
// string if the runtime rejects the currency code.
function formatCurrency(amount, currency) {
  const cur = (currency || "INR").toUpperCase();
  try {
    const nf = new Intl.NumberFormat(cur === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: cur,
      maximumFractionDigits: cur === "INR" ? 0 : 2,
    });
    return nf.format(Number(amount) || 0);
  } catch {
    return `${cur} ${Number(amount || 0).toLocaleString()}`;
  }
}

const HEALTH_META = {
  credited: { label: "CREDITED", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle },
  debited: { label: "DEBITED", tone: "bg-rose-100 text-rose-800 border-rose-300", Icon: Minus },
  stuck_pending: { label: "STUCK / NOT CREDITED", tone: "bg-rose-100 text-rose-800 border-rose-400", Icon: Warning },
  payment_failed: { label: "PAYMENT FAILED", tone: "bg-stone-200 text-stone-800 border-stone-400", Icon: XCircle },
  pending: { label: "PENDING", tone: "bg-amber-100 text-amber-900 border-amber-300", Icon: ClockClockwise },
};

function HealthPill({ health }) {
  const meta = HEALTH_META[health] || { label: health, tone: "bg-stone-100 text-stone-700 border-stone-300", Icon: Question };
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wider ${meta.tone}`}
      data-testid={`health-pill-${health}`}
    >
      <Icon size={11} weight="fill" /> {meta.label}
    </span>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(String(value));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-[10px] text-stone-600 hover:text-[#3F2D1B]"
      data-testid="copy-btn"
      aria-label="Copy to clipboard"
    >
      <Copy size={11} /> {copied ? "Copied" : "Copy"}
    </button>
  );
}

function BulkCreditPanel({ maxInr }) {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [amount, setAmount] = useState("1000");
  const [reason, setReason] = useState("Bulk admin top-up");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  // Stable idempotency prefix per panel mount — refresh-double-click
  // won't re-credit the same set.
  const [idemPrefix] = useState(
    () => `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  );

  // Split by comma / newline / semicolon. Trim. De-dupe case-insensitively.
  const identifiers = React.useMemo(() => {
    const parts = emailsRaw
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const p of parts) {
      const k = p.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(p); }
    }
    return out;
  }, [emailsRaw]);

  const amountNum = Number(amount);
  const validAmount =
    !Number.isNaN(amountNum) && amountNum > 0 && amountNum <= maxInr;
  const canSubmit =
    identifiers.length > 0 &&
    validAmount &&
    reason.trim().length >= 3 &&
    !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setResults(null);
    try {
      const { data } = await api.post("/admin/customer-credit/bulk", {
        identifiers,
        amount_inr: amountNum,
        reason: reason.trim(),
        idempotency_key_prefix: idemPrefix,
      });
      setResults(data);
      const nCredited = (data.results || []).filter((r) => r.status === "credited").length;
      const nAlready  = (data.results || []).filter((r) => r.status === "already_credited").length;
      const nMissing  = (data.results || []).filter((r) => r.status === "not_found" || r.status === "ambiguous").length;
      toast.success(
        `Credited ${nCredited} customer${nCredited === 1 ? "" : "s"}` +
        (nAlready ? ` · ${nAlready} already credited` : "") +
        (nMissing ? ` · ${nMissing} unresolved` : "") +
        ` · Total ₹${data.total_credited_inr}`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk credit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_PILL = {
    credited: "bg-emerald-100 text-emerald-800 border-emerald-300",
    already_credited: "bg-amber-100 text-amber-900 border-amber-300",
    not_found: "bg-rose-100 text-rose-800 border-rose-300",
    ambiguous: "bg-amber-100 text-amber-900 border-amber-300",
    error: "bg-rose-100 text-rose-800 border-rose-300",
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-5"
      data-testid="bulk-credit-panel"
    >
      <div className="flex items-center gap-2">
        <Wallet size={18} weight="fill" className="text-emerald-700" />
        <h2 className="font-serif-display text-xl text-[#3F2D1B]">
          Bulk top-up
        </h2>
        <span className="text-[10px] font-bold tracking-wider uppercase text-amber-800 bg-amber-200 px-2 py-0.5 rounded-full">
          Multi-customer
        </span>
      </div>
      <p className="text-[12px] text-stone-600 mt-1">
        Paste emails or names (one per line, or comma-separated) and grant the
        same top-up to everyone. Idempotent — retrying with the same list won&apos;t
        double-credit.
      </p>

      <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
            Emails or names
          </label>
          <textarea
            value={emailsRaw}
            onChange={(e) => setEmailsRaw(e.target.value)}
            placeholder={"alice@example.com\nbob@example.com, carol@example.com\nDeepak Sharma"}
            className="mt-1 w-full h-32 px-3 py-2 rounded-lg border border-amber-300 bg-white text-[#3F2D1B] text-sm resize-y focus:outline-none focus:border-[#3F2D1B]"
            data-testid="bulk-credit-identifiers"
          />
          <div className="text-[11px] text-stone-600 mt-1">
            {identifiers.length} unique customer{identifiers.length === 1 ? "" : "s"} detected.
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Amount per customer
            </label>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">₹</span>
              <input
                type="number"
                min="1"
                max={maxInr}
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-7 pr-3 py-2 rounded-lg border border-amber-300 bg-white text-[#3F2D1B] text-sm focus:outline-none focus:border-[#3F2D1B]"
                data-testid="bulk-credit-amount"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap mt-1.5">
              {[500, 1000, 2000, 5000].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 hover:bg-amber-200"
                >
                  ₹{q}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Reason (audit trail)
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Diwali goodwill top-up"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-[#3F2D1B] text-sm focus:outline-none focus:border-[#3F2D1B]"
              data-testid="bulk-credit-reason"
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-credit-submit"
          >
            <Plus size={14} weight="bold" />
            {submitting
              ? "CREDITING…"
              : `Add ₹${validAmount ? amountNum : "…"} top-up to ${identifiers.length || 0} customer${identifiers.length === 1 ? "" : "s"}`}
          </button>
          {!validAmount && amount && (
            <p className="text-[11px] text-rose-700 mt-2">
              Amount must be between ₹1 and ₹{maxInr}.
            </p>
          )}
        </div>
      </form>

      {results && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-white overflow-hidden" data-testid="bulk-credit-results">
          <div className="px-4 py-2 border-b border-amber-200 bg-amber-50 text-[11px] text-stone-700 font-semibold flex flex-wrap gap-x-4 gap-y-1">
            <span>Total credited: ₹{results.total_credited_inr}</span>
            <span>Amount each: ₹{results.amount_inr}</span>
            <span>Batch: <code className="text-[10px]">{results.bulk_prefix}</code></span>
          </div>
          <ul className="divide-y divide-amber-100">
            {(results.results || []).map((r, i) => (
              <li
                key={`${r.identifier}_${i}`}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm"
                data-testid={`bulk-credit-row-${r.status}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-[#3F2D1B]">
                    {r.name || r.identifier}
                  </div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {r.email || r.identifier}
                    {r.message && (
                      <span className="ml-1 text-rose-700">· {r.message}</span>
                    )}
                    {r.candidates?.length ? (
                      <span className="ml-1">· candidates: {r.candidates.join(", ")}</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {r.credited != null && (
                    <span className="text-[12px] text-emerald-800 font-semibold">
                      +₹{r.credited}
                    </span>
                  )}
                  {r.new_balance != null && (
                    <span className="text-[11px] text-stone-500">
                      → ₹{r.new_balance}
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border ${STATUS_PILL[r.status] || "bg-stone-100 text-stone-700 border-stone-300"}`}
                  >
                    {r.status.replace("_", " ")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}


function ScrapeBlocklistPanel() {
  // Anti-scrape blocklist + live rate-limit incidents. Auto-refreshes
  // every 20s so ops can spot a fresh scraper hit and promote them from
  // a temp-ban to a permanent block in one click.
  const [data, setData] = useState({ blocklist: [], incidents: [] });
  const [ip, setIp] = useState("");
  const [uaPrefix, setUaPrefix] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/scrape-blocklist");
      setData(data);
    } catch { /* not super-admin or transient — the outer page already gates */ }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [open, load]);

  const add = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!ip.trim() && !uaPrefix.trim()) {
      toast.error("Enter an IP or a User-Agent prefix.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/scrape-blocklist", {
        ip: ip.trim() || null,
        ua_prefix: uaPrefix.trim() || null,
        reason: reason.trim(),
      });
      toast.success("Blocked.");
      setIp(""); setUaPrefix(""); setReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add block.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove block for ${entry.ip || entry.ua_prefix}?`)) return;
    try {
      const params = {};
      if (entry.ip) params.ip = entry.ip;
      if (entry.ua_prefix) params.ua_prefix = entry.ua_prefix;
      await api.delete("/admin/scrape-blocklist", { params });
      toast.success("Removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove block.");
    }
  };

  const promoteIncident = async (inc) => {
    if (!inc?.ip) return;
    if (!window.confirm(`Permanently block ${inc.ip}?`)) return;
    try {
      await api.post("/admin/scrape-blocklist", {
        ip: inc.ip,
        reason: `Rate-limit trip on ${inc.endpoint} — ${inc.hits_in_window} hits in 60s`,
      });
      toast.success(`Blocked ${inc.ip}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not block IP.");
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-rose-200 bg-rose-50/40 overflow-hidden"
      data-testid="scrape-blocklist-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-rose-100/50"
        data-testid="scrape-blocklist-toggle"
      >
        <div className="flex items-center gap-2">
          <ShieldWarning size={20} weight="duotone" className="text-rose-700" />
          <div className="text-left">
            <div className="text-[11px] font-bold tracking-wider text-rose-800">ANTI-SCRAPE BLOCKLIST</div>
            <div className="text-xs text-stone-600">
              {open
                ? "Ban an IP or UA that's scraping our celebrity DB."
                : "Click to review who is scraping right now."}
            </div>
          </div>
        </div>
        <span className="text-[11px] font-bold text-stone-500">{open ? "HIDE" : "SHOW"}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-rose-200 pt-4">
          {/* Recent rate-limit incidents */}
          <div>
            <div className="text-[11px] font-bold tracking-wider text-stone-600 mb-2">
              RECENT AUTO-TRIP INCIDENTS ({data.incidents?.length || 0})
            </div>
            {data.incidents?.length ? (
              <ul className="space-y-1.5" data-testid="scrape-incidents">
                {data.incidents.map((inc, idx) => (
                  <li
                    key={`${inc.ip}-${inc.blocked_at}-${idx}`}
                    className="text-xs bg-white border border-rose-200 rounded-lg p-2 flex items-center gap-2 flex-wrap"
                  >
                    <span className="font-mono text-rose-800 font-semibold">{inc.ip}</span>
                    <span className="text-stone-500">{inc.hits_in_window} hits / 60s on {inc.endpoint}</span>
                    <span className="text-stone-400 ml-auto">{inc.blocked_at}</span>
                    <button
                      type="button"
                      onClick={() => promoteIncident(inc)}
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white hover:bg-rose-700"
                      data-testid={`promote-incident-${inc.ip}`}
                    >
                      BAN PERMANENTLY
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-stone-500 italic">No auto-trip incidents in recent memory.</p>
            )}
          </div>

          {/* Add form */}
          <form onSubmit={add} className="bg-white rounded-xl p-3 border border-rose-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="IP address (e.g. 203.0.113.45)"
                className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-mono"
                data-testid="scrape-block-ip"
              />
              <input
                type="text"
                value={uaPrefix}
                onChange={(e) => setUaPrefix(e.target.value)}
                placeholder="OR User-Agent prefix (case-insensitive)"
                className="px-3 py-2 rounded-lg border border-stone-300 text-sm"
                data-testid="scrape-block-ua"
              />
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (audit log)"
                className="px-3 py-2 rounded-lg border border-stone-300 text-sm"
                data-testid="scrape-block-reason"
              />
            </div>
            <button
              type="submit"
              disabled={busy || (!ip.trim() && !uaPrefix.trim())}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold tracking-wider hover:bg-rose-700 disabled:opacity-50"
              data-testid="scrape-block-submit"
            >
              {busy ? "BLOCKING…" : "ADD BLOCK"}
            </button>
          </form>

          {/* Current blocklist */}
          <div>
            <div className="text-[11px] font-bold tracking-wider text-stone-600 mb-2">
              PERMANENT BLOCKS ({data.blocklist?.length || 0})
            </div>
            {data.blocklist?.length ? (
              <ul className="space-y-1.5" data-testid="scrape-blocklist-entries">
                {data.blocklist.map((b, idx) => (
                  <li
                    key={`${b.ip || ""}-${b.ua_prefix || ""}-${idx}`}
                    className="text-xs bg-white border border-stone-200 rounded-lg p-2 flex items-center gap-2 flex-wrap"
                  >
                    {b.ip && <span className="font-mono text-rose-800 font-semibold">{b.ip}</span>}
                    {b.ua_prefix && <span className="font-mono text-stone-700 truncate max-w-xs">UA: {b.ua_prefix}</span>}
                    <span className="text-stone-500 truncate max-w-xs">{b.reason || "—"}</span>
                    <span className="text-stone-400 ml-auto">by {b.created_by || "?"}</span>
                    <button
                      type="button"
                      onClick={() => remove(b)}
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-200 text-stone-700 hover:bg-stone-300"
                      data-testid={`unblock-${b.ip || b.ua_prefix}`}
                    >
                      UNBLOCK
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-stone-500 italic">No permanent blocks yet.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


function BanHistoryPanel({ history, open, onToggle, accent = "rose" }) {
  // Small collapsible list of prior ban → unban cycles. Rendered inside
  // both the ban and unban action cards so ops can spot repeat offenders
  // at a glance. `history` is server-side chronological (oldest first);
  // we reverse for display so the freshest cycle sits at the top.
  const count = history?.length || 0;
  if (count === 0) return null;
  const rows = [...history].reverse();
  const toneOpen = accent === "orange" ? "text-orange-800" : "text-rose-800";
  const toneCard = accent === "orange"
    ? "bg-orange-50 border-orange-200"
    : "bg-rose-50 border-rose-200";
  return (
    <div className="mt-3" data-testid="ban-history-panel">
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1 text-[11px] font-bold tracking-wider ${toneOpen} hover:underline`}
        data-testid="ban-history-toggle"
      >
        {open ? "▾" : "▸"} BAN HISTORY ({count})
      </button>
      {open && (
        <ol
          className="mt-2 space-y-2"
          data-testid="ban-history-list"
        >
          {rows.map((h, i) => {
            const idx = count - i;  // display # from oldest (1) upward
            return (
              <li
                key={`${h.banned_at || i}-${h.unbanned_at || i}`}
                className={`text-xs rounded-lg border ${toneCard} p-2 space-y-1`}
                data-testid={`ban-history-row-${idx}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-rose-600 text-white">
                    CYCLE #{idx}
                  </span>
                  {h.banned_at && (
                    <span className="text-stone-500 font-mono">
                      {String(h.banned_at).slice(0, 10)} → {String(h.unbanned_at || "").slice(0, 10) || "—"}
                    </span>
                  )}
                </div>
                {h.ban_reason && (
                  <div className="text-rose-900">
                    <strong>Banned:</strong> {h.ban_reason}
                    {h.banned_by_admin && (
                      <span className="text-stone-500 ml-1">· by {h.banned_by_admin}</span>
                    )}
                  </div>
                )}
                {h.unban_reason && (
                  <div className="text-emerald-800">
                    <strong>Unbanned:</strong> {h.unban_reason}
                    {h.unbanned_by_admin && (
                      <span className="text-stone-500 ml-1">· by {h.unbanned_by_admin}</span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}


function ForceEndChatsPanel() {
  // Ops tool — enter an email, see the user's current chats + wallet
  // + VIP credit + super-admin flag, and force-end every active chat
  // in one click. Built after a real production report of a user
  // appearing to chat with ₹0 balance.
  const [email, setEmail] = React.useState("");
  const [days, setDays] = React.useState(7);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [ending, setEnding] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [banReason, setBanReason] = React.useState("");
  const [banning, setBanning] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const audit = async (e) => {
    e?.preventDefault?.();
    const q = email.trim();
    if (!q) return;
    setLoading(true);
    setData(null);
    setHistoryOpen(false);  // reset toggle when we load a fresh user
    try {
      const { data } = await api.get("/admin/user-activity-by-email",
        { params: { email: q, days } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load activity.");
    } finally {
      setLoading(false);
    }
  };

  const forceEnd = async () => {
    if (!data?.summary?.active_chats) return;
    const n = data.summary.active_chats;
    if (!window.confirm(`Force-end ${n} active chat${n > 1 ? "s" : ""} for ${data.customer.email}?`)) return;
    setEnding(true);
    try {
      const { data: res } = await api.post("/admin/chats/force-end-by-email", {
        email: data.customer.email,
        reason: reason.trim() || "Force-ended by admin (suspicious activity)",
      });
      toast.success(`Ended ${res.ended_count} chat${res.ended_count === 1 ? "" : "s"}.`);
      // Refresh so the panel shows the ended state.
      audit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not force-end.");
    } finally {
      setEnding(false);
    }
  };

  const banUser = async () => {
    if (!data?.customer?.email) return;
    const r = banReason.trim();
    if (r.length < 3) {
      toast.error("A ban reason is required (min 3 chars).");
      return;
    }
    if (!window.confirm(
      `SOFT-BAN ${data.customer.email}?\n\nTheir sessions will be revoked immediately and every active chat will be force-ended. Wallet, chats and PDFs are preserved so you can unban later.`
    )) return;
    setBanning(true);
    try {
      const { data: res } = await api.post("/admin/users/ban-by-email", {
        email: data.customer.email,
        reason: r,
        force_end_chats: true,
      });
      toast.success(
        `Banned. Revoked ${res.revoked_sessions} session${res.revoked_sessions === 1 ? "" : "s"} and ended ${res.ended_count} chat${res.ended_count === 1 ? "" : "s"}.`
      );
      setBanReason("");
      audit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not ban.");
    } finally {
      setBanning(false);
    }
  };

  const unbanUser = async () => {
    if (!data?.customer?.email) return;
    if (!window.confirm(`UNBAN ${data.customer.email} and restore access?`)) return;
    setBanning(true);
    try {
      await api.post("/admin/users/unban-by-email", {
        email: data.customer.email,
        reason: banReason.trim() || "",
      });
      toast.success("Unbanned. User can log in again.");
      setBanReason("");
      audit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unban.");
    } finally {
      setBanning(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-orange-200 bg-orange-50/40 overflow-hidden"
      data-testid="force-end-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-orange-100/50"
        data-testid="force-end-toggle"
      >
        <div className="flex items-center gap-2">
          <XCircle size={20} weight="duotone" className="text-orange-700" />
          <div className="text-left">
            <div className="text-[11px] font-bold tracking-wider text-orange-800">USER ACTIVITY AUDIT & FORCE-END CHATS</div>
            <div className="text-xs text-stone-600">
              {open
                ? "Enter an email to see live chat + wallet activity, then force-end active sessions."
                : "Click when a user looks like they're chatting on ₹0 balance."}
            </div>
          </div>
        </div>
        <span className="text-[11px] font-bold text-stone-500">{open ? "HIDE" : "SHOW"}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-orange-200 pt-4">
          <form onSubmit={audit} className="bg-white rounded-xl p-3 border border-orange-200 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="text-[10px] font-bold tracking-wider text-stone-600 uppercase">User email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. liza_talwar_85@yahoo.com"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-300 text-sm"
                data-testid="force-end-email"
                required
              />
            </div>
            <div>
              <label className="text-[10px] font-bold tracking-wider text-stone-600 uppercase">Days</label>
              <input
                type="number"
                value={days}
                onChange={(e) => setDays(Number(e.target.value) || 7)}
                min={1}
                max={90}
                className="mt-1 w-20 px-3 py-2 rounded-lg border border-stone-300 text-sm"
                data-testid="force-end-days"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="px-4 py-2 rounded-full bg-stone-800 text-white text-[11px] font-bold tracking-wider hover:bg-stone-900 disabled:opacity-50"
              data-testid="force-end-audit-btn"
            >
              {loading ? "LOADING…" : "AUDIT ACTIVITY"}
            </button>
          </form>

          {data && (
            <div className="space-y-3" data-testid="force-end-audit-result">
              {/* Customer summary */}
              <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#3F2D1B] truncate">{data.customer.name}</div>
                  <div className="text-xs text-stone-500 truncate">{data.customer.email}</div>
                </div>
                <div className="text-xs">
                  <span className="text-stone-500">Wallet: </span>
                  <strong className="text-[#3F2D1B]">₹{data.customer.wallet_balance}</strong>
                </div>
                {data.customer.vip_credit_remaining > 0 && (
                  <div className="text-xs">
                    <span className="text-stone-500">VIP credit: </span>
                    <strong className="text-amber-700">₹{data.customer.vip_credit_remaining}</strong>
                  </div>
                )}
                {data.customer.is_super_admin && (
                  <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
                    SUPER-ADMIN (chats free!)
                  </span>
                )}
                {data.customer.vip_unlocks_all && (
                  <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    VIP ALL-UNLOCK
                  </span>
                )}
                {data.customer.banned && (
                  <span
                    className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-600 text-white"
                    data-testid="ban-status-badge"
                  >
                    BANNED
                  </span>
                )}
              </div>

              {/* Ban / unban card. Shows unban-panel if already banned,
                  soft-ban card otherwise. Keep this ABOVE force-end so
                  the biggest-hammer action is closest to the profile. */}
              {data.customer.banned ? (
                <div
                  className="bg-rose-100 border-2 border-rose-400 rounded-xl p-3"
                  data-testid="unban-panel"
                >
                  <div className="text-sm text-rose-900 mb-2">
                    <strong>Account suspended.</strong>
                    {data.customer.ban_reason && (
                      <span> Reason: <em>{data.customer.ban_reason}</em>.</span>
                    )}
                    {data.customer.banned_by_admin && (
                      <span className="text-xs text-rose-800/80"> · by {data.customer.banned_by_admin}</span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Unban reason (optional)"
                    className="w-full px-3 py-2 rounded-lg border border-rose-300 text-sm mb-2"
                    data-testid="unban-reason"
                  />
                  <button
                    type="button"
                    onClick={unbanUser}
                    disabled={banning}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold tracking-wider hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="unban-btn"
                  >
                    <CheckCircle size={12} weight="fill" />
                    {banning ? "UNBANNING…" : "UNBAN USER"}
                  </button>
                  <BanHistoryPanel
                    history={data.customer.ban_history}
                    open={historyOpen}
                    onToggle={() => setHistoryOpen((v) => !v)}
                    accent="rose"
                  />
                </div>
              ) : !data.customer.is_super_admin ? (
                <div
                  className="bg-orange-50 border border-orange-300 rounded-xl p-3"
                  data-testid="ban-panel"
                >
                  <div className="text-sm text-orange-900 mb-2">
                    <strong>Soft-ban this user?</strong> Revokes every session, force-ends every active chat, blocks future logins. Wallet + PDFs + chat history are preserved so you can unban cleanly later.
                  </div>
                  {(data.customer.ban_history?.length || 0) > 0 && (
                    <div className="mb-2 text-[11px] text-rose-800 font-bold">
                      ⚠ Repeat offender — this user has been banned {data.customer.ban_history.length} time{data.customer.ban_history.length === 1 ? "" : "s"} before.
                    </div>
                  )}
                  <input
                    type="text"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Ban reason (required, min 3 chars — for audit log)"
                    className="w-full px-3 py-2 rounded-lg border border-orange-300 text-sm mb-2"
                    data-testid="ban-reason"
                  />
                  <button
                    type="button"
                    onClick={banUser}
                    disabled={banning || banReason.trim().length < 3}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-rose-700 text-white text-[11px] font-bold tracking-wider hover:bg-rose-800 disabled:opacity-50"
                    data-testid="ban-btn"
                  >
                    <ShieldWarning size={12} weight="fill" />
                    {banning ? "BANNING…" : "SOFT-BAN THIS USER"}
                  </button>
                  <BanHistoryPanel
                    history={data.customer.ban_history}
                    open={historyOpen}
                    onToggle={() => setHistoryOpen((v) => !v)}
                    accent="orange"
                  />
                </div>
              ) : (
                <div className="text-xs text-stone-500 italic">
                  This user is a super-admin — banning is disabled.
                </div>
              )}

              {/* Force-end action */}
              {data.summary.active_chats > 0 ? (
                <div className="bg-rose-50 border border-rose-300 rounded-xl p-3">
                  <div className="text-sm text-rose-900 mb-2">
                    <strong>{data.summary.active_chats}</strong> active chat{data.summary.active_chats > 1 ? "s" : ""} — force-end?
                  </div>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (for audit log)"
                    className="w-full px-3 py-2 rounded-lg border border-rose-300 text-sm mb-2"
                    data-testid="force-end-reason"
                  />
                  <button
                    type="button"
                    onClick={forceEnd}
                    disabled={ending}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold tracking-wider hover:bg-rose-700 disabled:opacity-50"
                    data-testid="force-end-btn"
                  >
                    <XCircle size={12} weight="fill" />
                    {ending ? "ENDING…" : `FORCE-END ${data.summary.active_chats} CHAT${data.summary.active_chats > 1 ? "S" : ""}`}
                  </button>
                </div>
              ) : (
                <div className="text-xs text-stone-500 italic">No active chats — nothing to force-end.</div>
              )}

              {/* Chat list */}
              {data.chats?.length ? (
                <ul className="space-y-1.5" data-testid="force-end-chat-list">
                  {data.chats.map((c) => {
                    const secs = c.elapsed_seconds_now || 0;
                    const mm = Math.floor(secs / 60);
                    const ss = secs % 60;
                    return (
                      <li
                        key={c.chat_id}
                        className={`text-xs rounded-lg border p-2 flex items-center gap-2 flex-wrap ${
                          c.status === "ended" ? "border-stone-200 bg-stone-50/60" : "border-emerald-300 bg-emerald-50/60"
                        }`}
                      >
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          c.status === "ended" ? "bg-stone-200 text-stone-700" : "bg-emerald-500 text-white"
                        }`}>
                          {c.status?.toUpperCase() || "?"}
                        </span>
                        <span className="text-[#3F2D1B]">{c.astrologer?.name || "—"}</span>
                        {c.astrologer?.is_ai && (
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-sky-100 text-sky-800">AI</span>
                        )}
                        <span className="text-stone-500">₹{c.astrologer?.rate_per_min || 0}/min</span>
                        <span className="text-stone-500">· charged ₹{c.total_charged}</span>
                        <span className="text-stone-500">· billed {c.billed_minutes || 0}min</span>
                        {c.status !== "ended" && (
                          <span className="text-emerald-700 ml-auto">now: {mm}m {ss}s</span>
                        )}
                        {c.end_reason && (
                          <span className="text-stone-400 truncate max-w-xs">reason: {c.end_reason}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-xs text-stone-500 italic">No chats in the last {data.summary.days_window} days.</div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


export default function AdminCustomerLookupPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [maxCreditInr, setMaxCreditInr] = useState(10000);

  React.useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  // Fetch the server-side credit cap once the page mounts.
  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/admin/customer-credit/config")
      .then(({ data }) => setMaxCreditInr(Number(data?.max_inr) || 10000))
      .catch(() => { /* keep default */ });
  }, [user]);

  const refreshOne = async (userId) => {
    try {
      const { data } = await api.get("/admin/customer-lookup", {
        params: { q: userId, limit },
      });
      const fresh = (data.results || [])[0];
      if (!fresh) return;
      setResults((prev) => prev.map((r) => (r.user_id === userId ? fresh : r)));
    } catch { /* noop */ }
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) {
      setError("Type an email, name or user_id to look up.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/customer-lookup", {
        params: { q, limit },
      });
      setResults(data.results || []);
      setSearched(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <PageShell><Header />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center text-muted">Loading…</div>
        <Footer />
      </PageShell>
    );
  }
  if (!user || !user.is_super_admin) {
    return (
      <PageShell><Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="admin-customer-lookup-denied">
          <ShieldWarning size={36} weight="duotone" className="mx-auto text-rose-600" />
          <h1 className="font-serif-display text-3xl mt-4 font-light text-[#3F2D1B]">Super-admin only</h1>
          <p className="text-muted mt-3 font-light max-w-md mx-auto leading-relaxed">
            This page lets AstroKuber super-admins inspect a customer's last transactions and wallet.
          </p>
          <Link to="/" className="inline-block mt-7 px-6 py-2.5 rounded-full bg-[#3F2D1B] text-amber-50 text-sm font-bold tracking-wider">
            Back to home
          </Link>
        </div>
        <Footer />
      </PageShell>
    );
  }

  return (
    <PageShell><Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-customer-lookup-page">
        <header>
          <div className="label-eyebrow text-[#7C2D12]">Super-admin · Payments support</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 font-light leading-tight text-[#3F2D1B]">
            Customer payment lookup
          </h1>
          <p className="text-muted mt-3 font-light text-sm max-w-2xl leading-relaxed">
            Type an <span className="font-bold">email</span>, <span className="font-bold">name</span> or
            {" "}<span className="font-bold">user_id</span> to see the last few wallet transactions for that
            customer — flagged for failures, mismatches or payments that were captured but never credited
            (the EU PayPal "payment mismatch" bug class).
          </p>
        </header>

        <form
          onSubmit={runSearch}
          className="mt-7 flex flex-wrap items-stretch gap-2"
          data-testid="customer-lookup-form"
        >
          <div className="flex-1 min-w-[240px] relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="patricia@patricianatalia.com  ·  Patricia  ·  user_abc123"
              className="w-full pl-9 pr-3 py-3 rounded-full border border-amber-300 bg-white text-[#3F2D1B] text-sm focus:outline-none focus:border-[#3F2D1B]"
              data-testid="customer-lookup-input"
            />
          </div>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-white border border-amber-300 rounded-full text-xs font-bold tracking-wider px-3 py-2 text-[#3F2D1B]"
            data-testid="customer-lookup-limit"
            aria-label="Number of recent transactions to show"
          >
            {[5, 10, 20, 30, 50].map((n) => <option key={n} value={n}>LAST {n}</option>)}
          </select>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#3F2D1B] text-amber-50 text-xs font-bold tracking-wider hover:bg-[#2c1f12] disabled:opacity-60"
            data-testid="customer-lookup-submit"
          >
            <ArrowsClockwise size={14} weight="bold" /> {loading ? "SEARCHING…" : "LOOKUP"}
          </button>
        </form>

        <BulkCreditPanel maxInr={maxCreditInr} />

        <ScrapeBlocklistPanel />

        <ForceEndChatsPanel />

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="customer-lookup-error">
            {error}
          </div>
        )}

        {searched && !loading && results.length === 0 && !error && (
          <div className="mt-10 rounded-2xl p-10 bg-amber-50 border border-amber-200 text-center" data-testid="customer-lookup-empty">
            <p className="font-serif-display text-2xl font-light text-[#3F2D1B]">No customer matched “{query}”.</p>
            <p className="text-muted mt-2 text-sm font-light">
              Try a fuller email, a more specific name, or the exact <code>user_id</code>.
            </p>
          </div>
        )}

        <div className="mt-7 space-y-6">
          {results.map((r) => (
            <CustomerCard
              key={r.user_id}
              data={r}
              maxCreditInr={maxCreditInr}
              onCredited={() => refreshOne(r.user_id)}
            />
          ))}
        </div>
      </main>
      <Footer />
    </PageShell>
  );
}

function CustomerCard({ data, maxCreditInr, onCredited }) {
  const s = data.summary || {};
  const hasIssue = s.has_stuck_orders || s.has_failed_orders;
  const [creditOpen, setCreditOpen] = useState(false);
  const [debitOpen, setDebitOpen] = useState(false);
  return (
    <article
      className={`rounded-3xl border bg-white overflow-hidden ${hasIssue ? "border-rose-300 shadow-[0_4px_20px_rgba(225,29,72,0.08)]" : "border-amber-200"}`}
      data-testid={`customer-card-${data.user_id}`}
    >
      {/* Header strip */}
      <div className="p-5 sm:p-6 flex items-start gap-4 border-b border-stone-200/70">
        {data.picture ? (
          <img src={data.picture} alt={data.name} className="w-14 h-14 rounded-full object-cover border border-amber-200" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <UserCircle size={56} weight="duotone" className="text-stone-400" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B] truncate">{data.name}</h2>
            {data.free_trial_used && (
              <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">FREE TRIAL USED</span>
            )}
            {data.vip_credit_remaining > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-950">
                <Crown size={10} weight="fill" /> VIP · ₹{data.vip_credit_remaining}
              </span>
            )}
          </div>
          {data.email && (
            <a href={`mailto:${data.email}`} className="text-sm text-muted underline mt-0.5 inline-block">{data.email}</a>
          )}
          <div className="mt-1 text-[11px] text-stone-500 font-mono inline-flex items-center gap-2">
            user_id: {data.user_id} <CopyButton value={data.user_id} />
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="label-eyebrow text-muted">Current wallet</div>
          <div className="font-serif-display text-3xl text-[#3F2D1B] inline-flex items-center gap-1.5">
            <Wallet size={20} weight="duotone" /> ₹{data.wallet_balance}
          </div>
          <div className="text-[11px] text-stone-500">
            joined {fmtDateTime(data.created_at)}
          </div>
          {/* Lifetime paid — per currency, so a US customer's $ payments
              and an India customer's ₹ payments don't get lumped into a
              misleading INR-equivalent number. Excludes manual admin
              credits, stuck payments and failed payments — this is real
              money the customer paid. */}
          {(s.total_paid_by_currency || []).length > 0 && (
            <div
              className="mt-2 flex items-center justify-end gap-1.5 flex-wrap"
              data-testid={`total-paid-${data.user_id}`}
            >
              {s.total_paid_by_currency.map((t) => (
                <span
                  key={t.currency}
                  title={`${t.orders} successful order${t.orders === 1 ? "" : "s"} in ${t.currency}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF7E5] border border-[#FFB800]/40 text-[11px] font-bold tracking-wider text-[#7A5A0C]"
                  data-testid={`total-paid-${t.currency}-${data.user_id}`}
                >
                  Paid {formatCurrency(t.amount, t.currency)}
                  <span className="text-stone-500 font-normal">· {t.orders}</span>
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center justify-end gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setCreditOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold tracking-wider hover:bg-emerald-700"
              data-testid={`open-credit-modal-${data.user_id}`}
            >
              <Plus size={12} weight="bold" /> CREDIT WALLET
            </button>
            <button
              type="button"
              onClick={() => setDebitOpen(true)}
              disabled={!(data.wallet_balance > 0)}
              title={data.wallet_balance > 0 ? "Deduct money from this wallet" : "Wallet is empty — nothing to debit"}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold tracking-wider hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`open-debit-modal-${data.user_id}`}
            >
              <Minus size={12} weight="bold" /> DEBIT WALLET
            </button>
          </div>
        </div>
      </div>

      {/* Health summary */}
      {hasIssue && (
        <div className="px-5 sm:px-6 pt-4 pb-1 flex items-start gap-3 bg-rose-50/60 border-b border-rose-200/60">
          <Warning size={18} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />
          <div className="text-sm text-rose-900">
            <strong>Action needed.</strong>{" "}
            {s.has_stuck_orders && (
              <span>
                {data.orders.filter((o) => o.health === "stuck_pending").length} payment(s) captured by Razorpay but never credited to this wallet (~₹{s.stuck_total_inr}). Cross-check the order_id(s) below in your Razorpay dashboard and either refund or manually credit.
              </span>
            )}
            {s.has_failed_orders && !s.has_stuck_orders && (
              <span>Signature/verification failures recorded — usually customer-side (popup closed, card declined). Ask them to retry.</span>
            )}
          </div>
        </div>
      )}

      {/* Orders */}
      <div className="p-5 sm:p-6">
        <div className="text-[11px] tracking-wider uppercase text-stone-500 mb-3 font-bold">
          Last {data.orders.length} transactions
        </div>
        {data.orders.length === 0 ? (
          <p className="text-sm text-muted italic">No wallet orders on record for this customer.</p>
        ) : (
          <ul className="space-y-2.5" data-testid={`orders-list-${data.user_id}`}>
            {data.orders.map((o) => <OrderRow key={o.order_id} o={o} />)}
          </ul>
        )}
        <div className="mt-4 pt-3 border-t border-stone-200 text-[11px] text-stone-500 flex flex-wrap gap-4">
          <span>Credited shown: <strong className="text-[#3F2D1B]">₹{s.credited_total_inr || 0}</strong></span>
          {s.debited_total_inr > 0 && (
            <span>Debited shown: <strong className="text-rose-700">₹{s.debited_total_inr}</strong></span>
          )}
          {s.stuck_total_inr > 0 && (
            <span>Stuck (not credited): <strong className="text-rose-700">₹{s.stuck_total_inr}</strong></span>
          )}
        </div>
      </div>

      {creditOpen && (
        <CreditWalletModal
          customer={data}
          maxInr={maxCreditInr}
          onClose={() => setCreditOpen(false)}
          onSuccess={() => {
            setCreditOpen(false);
            onCredited?.();
          }}
        />
      )}
      {debitOpen && (
        <DebitWalletModal
          customer={data}
          maxInr={maxCreditInr}
          onClose={() => setDebitOpen(false)}
          onSuccess={() => {
            setDebitOpen(false);
            onCredited?.();
          }}
        />
      )}
    </article>
  );
}

function CreditWalletModal({ customer, maxInr, onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Stable idempotency key per modal mount — refresh-double-click won't double-credit.
  const [idemKey] = useState(() => `manual_${customer.user_id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const amountNum = Number(amount);
  const valid =
    !Number.isNaN(amountNum) &&
    amountNum > 0 &&
    amountNum <= maxInr &&
    reason.trim().length >= 3;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/customer-credit", {
        user_id: customer.user_id,
        amount_inr: amountNum,
        reason: reason.trim(),
        idempotency_key: idemKey,
      });
      if (data?.already_credited) {
        toast("Already credited earlier with this key — wallet not changed.");
      } else {
        toast.success(`Credited ₹${data.credited} → ${customer.name}. New balance ₹${data.balance}.`);
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not credit wallet.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid={`credit-modal-${customer.user_id}`}
    >
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-amber-200 overflow-hidden"
      >
        <div className="flex items-start justify-between p-5 border-b border-stone-200">
          <div>
            <div className="label-eyebrow text-emerald-700">Manual credit</div>
            <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B] mt-1">
              Credit {customer.name}'s wallet
            </h3>
            <p className="text-[11px] text-stone-500 mt-1">
              Current balance ₹{customer.wallet_balance} · cap ₹{maxInr} per credit
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-stone-100"
            aria-label="Close"
            data-testid="credit-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Amount in ₹
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="1"
              max={maxInr}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
              data-testid="credit-modal-amount"
              autoFocus
            />
            <div className="mt-1 flex gap-2 flex-wrap">
              {[100, 500, 1000, 2000, 5000].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 hover:bg-amber-200"
                  data-testid={`credit-quick-${q}`}
                >
                  ₹{q}
                </button>
              ))}
            </div>
            {amountNum > maxInr && (
              <p className="text-[11px] text-rose-700 mt-1">Maximum is ₹{maxInr} per credit.</p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Reason <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Backfill stuck PayPal order_XXXX, apology credit for missed chat, etc."
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 text-[#3F2D1B] text-sm focus:outline-none focus:border-[#3F2D1B] resize-none"
              data-testid="credit-modal-reason"
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Required (≥ 3 chars). Stored in the audit log against your admin email.
            </p>
          </div>
        </div>

        <div className="p-5 pt-0 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-stone-700 text-xs font-bold tracking-wider hover:bg-stone-100"
            data-testid="credit-modal-cancel"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={!valid || submitting}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold tracking-wider hover:bg-emerald-700 disabled:opacity-50"
            data-testid="credit-modal-submit"
          >
            {submitting ? "CREDITING…" : "CREDIT WALLET"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DebitWalletModal({ customer, maxInr, onClose, onSuccess }) {
  // Mirror of CreditWalletModal — deducts money from wallet. Kept as a
  // separate component (rather than one modal with a mode prop) so the
  // styling, validation, and copy stay unambiguous for the operator.
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idemKey] = useState(() => `manual_debit_${customer.user_id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const currentBalance = Number(customer.wallet_balance || 0);
  // Effective cap = the tighter of the ₹10k server cap and the customer's
  // current balance (server refuses to overdraw).
  const effectiveMax = Math.min(maxInr, currentBalance);
  const amountNum = Number(amount);
  const valid =
    !Number.isNaN(amountNum) &&
    amountNum > 0 &&
    amountNum <= effectiveMax &&
    reason.trim().length >= 3;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/customer-debit", {
        user_id: customer.user_id,
        amount_inr: amountNum,
        reason: reason.trim(),
        idempotency_key: idemKey,
      });
      if (data?.already_debited) {
        toast("Already debited earlier with this key — wallet not changed.");
      } else {
        toast.success(`Debited ₹${data.debited} from ${customer.name}. New balance ₹${data.balance}.`);
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not debit wallet.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid={`debit-modal-${customer.user_id}`}
    >
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-rose-200 overflow-hidden"
      >
        <div className="flex items-start justify-between p-5 border-b border-stone-200">
          <div>
            <div className="label-eyebrow text-rose-700">Manual debit</div>
            <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B] mt-1">
              Debit {customer.name}'s wallet
            </h3>
            <p className="text-[11px] text-stone-500 mt-1">
              Current balance ₹{currentBalance} · max debit ₹{effectiveMax}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-stone-100"
            aria-label="Close"
            data-testid="debit-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Amount in ₹
            </label>
            <input
              type="number"
              inputMode="decimal"
              min="1"
              max={effectiveMax}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-rose-300 text-[#3F2D1B] focus:outline-none focus:border-rose-600"
              data-testid="debit-modal-amount"
              autoFocus
            />
            <div className="mt-1 flex gap-2 flex-wrap">
              {[100, 500, 1000, 2000, 5000]
                .filter((q) => q <= effectiveMax)
                .map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setAmount(String(q))}
                    className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-rose-100 text-rose-900 hover:bg-rose-200"
                    data-testid={`debit-quick-${q}`}
                  >
                    ₹{q}
                  </button>
                ))}
              {currentBalance > 0 && (
                <button
                  type="button"
                  onClick={() => setAmount(String(currentBalance))}
                  className="text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-rose-200 text-rose-950 hover:bg-rose-300"
                  data-testid="debit-quick-max"
                >
                  Full balance
                </button>
              )}
            </div>
            {amountNum > effectiveMax && (
              <p className="text-[11px] text-rose-700 mt-1">
                {amountNum > currentBalance
                  ? `Cannot exceed wallet balance ₹${currentBalance}.`
                  : `Maximum is ₹${maxInr} per debit.`}
              </p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
              Reason <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Reversing accidental over-credit, chargeback claw-back, fraud reversal, etc."
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-rose-300 text-[#3F2D1B] text-sm focus:outline-none focus:border-rose-600 resize-none"
              data-testid="debit-modal-reason"
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Required (≥ 3 chars). Stored in the audit log against your admin email.
            </p>
          </div>
        </div>

        <div className="p-5 pt-0 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-stone-700 text-xs font-bold tracking-wider hover:bg-stone-100"
            data-testid="debit-modal-cancel"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={!valid || submitting}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-rose-600 text-white text-xs font-bold tracking-wider hover:bg-rose-700 disabled:opacity-50"
            data-testid="debit-modal-submit"
          >
            {submitting ? "DEBITING…" : "DEBIT WALLET"}
          </button>
        </div>
      </form>
    </div>
  );
}

function OrderRow({ o }) {
  const isUsd = o.currency === "USD";
  const symbol = isUsd ? "$" : "₹";
  const displayValue = isUsd ? o.display_amount : o.amount_inr;
  const isManualCredit = o.provider === "admin_manual" && o.purpose === "admin_manual_credit";
  const isManualDebit  = o.provider === "admin_manual" && o.purpose === "admin_manual_debit";
  const isManual = isManualCredit || isManualDebit;
  const purposeLabel = isManualDebit
    ? "DEBIT"
    : isManualCredit
      ? "MANUAL"
      : (o.purpose === "vip_king_pack" ? "VIP KING" : "TOPUP");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  // Try to auto-recover a stuck PayPal/Razorpay wallet top-up. Idempotent.
  // If Razorpay confirms a captured payment matching the order amount,
  // this credits the wallet without any manual "admin_customer_credit"
  // step. Common cause: buyer's PayPal email ≠ AstroKuberChat account
  // email — the client-side handler never fires so /verify is skipped.
  const runReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    setReconcileResult(null);
    try {
      const { data } = await api.post("/super-admin/wallet/reconcile", {
        razorpay_order_id: o.order_id,
      });
      if (data?.already_credited) {
        toast(`Already credited earlier. Balance unchanged.`);
      } else if (data?.credited === false) {
        toast.error(data?.detail || "No matching captured payment on this order yet.");
      } else {
        toast.success(`Reconciled ${o.currency === "USD" ? "$" : "₹"}${data.credited?.toFixed(2)}. Refresh the lookup to see the new balance.`);
      }
      setReconcileResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reconcile this order.");
    } finally {
      setReconciling(false);
    }
  };

  return (
    <li
      className={`rounded-xl border p-3.5 flex items-start gap-3 flex-wrap ${
        isManualDebit
          ? "border-rose-200 bg-rose-50/40"
          : isManualCredit
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-stone-200 bg-white"
      }`}
      data-testid={`order-row-${o.order_id}`}
    >
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <HealthPill health={o.health} />
          <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${
            isManualDebit
              ? "bg-rose-600 text-white"
              : isManualCredit
                ? "bg-emerald-600 text-white"
                : "bg-stone-100 text-stone-700"
          }`}>
            {purposeLabel}
          </span>
          {!isManual && (
            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
              {o.currency}
            </span>
          )}
        </div>
        <div className="mt-1.5 text-sm text-[#3F2D1B]">
          <span className={`font-serif-display text-lg ${isManualDebit ? "text-rose-700" : ""}`}>
            {isManualDebit ? "−" : ""}{symbol}{displayValue ?? "—"}
          </span>
          {isUsd && (
            <span className="text-[11px] text-stone-500 ml-2">
              (≈ ₹{o.amount_inr} will be credited to wallet)
            </span>
          )}
          {o.bonus > 0 && (
            <span className="text-[11px] text-emerald-700 ml-2">+ ₹{o.bonus} bonus</span>
          )}
          {o.coupon_code && (
            <span className="text-[11px] text-amber-800 ml-2">
              + ₹{o.coupon_bonus || 0} ({o.coupon_code})
            </span>
          )}
        </div>
        {isManual && (o.reason || o.granted_by_admin) && (
          <div className={`mt-1.5 text-[11px] rounded px-2 py-1 inline-block max-w-full break-words ${
            isManualDebit ? "text-rose-900 bg-rose-100/60" : "text-emerald-900 bg-emerald-100/60"
          }`}>
            {o.reason && <span><strong>Reason:</strong> {o.reason}</span>}
            {o.granted_by_admin && (
              <span className={`ml-2 ${isManualDebit ? "text-rose-700" : "text-emerald-700"}`}>· by {o.granted_by_admin}</span>
            )}
          </div>
        )}
        <div className="mt-1 text-[10px] text-stone-500 font-mono break-all">
          order_id: {o.order_id} <CopyButton value={o.order_id} />
          {o.payment_id && (
            <span className="ml-2">· pay: {o.payment_id} <CopyButton value={o.payment_id} /></span>
          )}
        </div>
        {/* Reconcile CTA — only for stuck (captured on Razorpay but not
            credited) orders. Safe to click multiple times: the backend
            only credits when Razorpay itself confirms a matching captured
            payment on the order. */}
        {o.health === "stuck_pending" && !isManual && (
          <div className="mt-2.5">
            <button
              type="button"
              onClick={runReconcile}
              disabled={reconciling}
              className="rounded-full px-3 py-1.5 text-[11px] font-semibold bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-60 flex items-center gap-1.5"
              data-testid={`reconcile-order-${o.order_id}`}
            >
              {reconciling ? "Checking Razorpay…" : "Auto-recover from Razorpay"}
            </button>
            {reconcileResult && reconcileResult.credited === false && reconcileResult.payments_found?.length > 0 && (
              <div className="mt-1.5 text-[10px] text-rose-700 font-mono">
                {reconcileResult.payments_found.length} payment(s) found: {reconcileResult.payments_found.map((p) => `${p.status} ${p.amount}${p.currency}`).join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-right text-[11px] text-stone-600 min-w-[120px]">
        <div>{fmtDateTime(o.created_at)}</div>
        <div className="text-stone-500">{fmtAge(o.age_minutes)}</div>
        {o.credited_at && (
          <div className="text-emerald-700 mt-1">credited {fmtDateTime(o.credited_at)}</div>
        )}
        {o.verified_at && !o.credited_at && (
          <div className="text-rose-700 mt-1">verified-failed {fmtDateTime(o.verified_at)}</div>
        )}
      </div>
    </li>
  );
}
